import { orderBy } from 'lodash'
import { db, firebase, storageRef } from '../../../../config/firebase'
import { updateDoc } from '../../../../utils/db/updateDoc'
import { CATALOG_TAXONOMY_COLLECTION, NODE_TYPE, expectedParentType } from './catalogTaxonomyConstants'
import {
  flagsForArchiveWrite,
  normalizeCatalogTaxonomyFlags,
  splitNodesByArchive,
} from './catalogTaxonomyFlagRules'
import {
  isCatalogTaxonomySlugTaken,
  isValidCatalogTaxonomySlug,
  slugifyCatalogTaxonomyName,
} from './catalogTaxonomySlug'
import {
  assembleDeletePreflight,
  assertCanAddNonArchivedChildUnderParent,
  collectSubtreeBlockers,
} from './catalogTaxonomyDeletePreflight'
import { cleanupCatalogTaxonomyStoragePaths, collectCatalogTaxonomyStoragePaths } from './catalogTaxonomyStorageCleanup'
import writeCatalogTaxonomySlug from '../../../../api/products/writeCatalogTaxonomySlug'

const serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp()

const toNode = (doc) => ({ id: doc.id, ...doc.data() })

export function subscribeCatalogTaxonomySiblings(parentId, onChange, onError) {
  let query = db.collection(CATALOG_TAXONOMY_COLLECTION)

  if (parentId === null || parentId === undefined) {
    query = query.where('parentId', '==', null)
  } else {
    query = query.where('parentId', '==', parentId)
  }

  return query.onSnapshot(
    (snap) => {
      const nodes = []
      snap.forEach((doc) => nodes.push(toNode(doc)))
      onChange(nodes)
    },
    (error) => {
      if (onError) onError(error)
    }
  )
}

export function partitionSiblingLists(nodes = []) {
  const { nonArchived, archived } = splitNodesByArchive(nodes)
  return {
    nonArchived: orderBy(nonArchived, ['sortOrder', 'name'], ['asc', 'asc']),
    archived: orderBy(archived, ['name'], ['asc']),
  }
}

export function nextSortOrder(nonArchivedSiblings = []) {
  if (!nonArchivedSiblings.length) return 0
  const max = Math.max(...nonArchivedSiblings.map((n) => Number(n.sortOrder) || 0))
  return max + 1
}

async function compactNonArchivedSortOrders(nonArchivedSiblings = []) {
  const ordered = orderBy(nonArchivedSiblings, ['sortOrder', 'name'], ['asc', 'asc'])
  const batch = db.batch()
  let writes = 0

  ordered.forEach((node, index) => {
    if (Number(node.sortOrder) === index) return
    const ref = db.collection(CATALOG_TAXONOMY_COLLECTION).doc(node.id)
    batch.update(ref, {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    })
    writes += 1
  })

  if (writes > 0) {
    await batch.commit()
  }
}

export async function reorderNonArchivedSiblings(orderedIds = []) {
  const batch = db.batch()
  orderedIds.forEach((id, index) => {
    const ref = db.collection(CATALOG_TAXONOMY_COLLECTION).doc(id)
    batch.update(ref, {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    })
  })
  if (orderedIds.length) {
    await batch.commit()
  }
}

function assertSlugAvailable(slug, siblings, excludeNodeId) {
  if (!isValidCatalogTaxonomySlug(slug)) {
    throw new Error('Некоректний slug. Використовуйте латиницю, цифри та дефіси.')
  }
  if (isCatalogTaxonomySlugTaken(slug, siblings, excludeNodeId)) {
    throw new Error('Такий slug уже зайнятий серед siblings (включно з архівом).')
  }
}

async function fetchChildren(parentId) {
  const snap = await db.collection(CATALOG_TAXONOMY_COLLECTION).where('parentId', '==', parentId).get()
  const nodes = []
  snap.forEach((doc) => nodes.push(toNode(doc)))
  return nodes
}

/**
 * Load full subtree documents for cascade delete (depth ≤ 2).
 */
export async function collectCatalogTaxonomySubtree(root) {
  if (!root?.id) return []

  if (root.type === NODE_TYPE.SUBCATEGORY) {
    return [root]
  }

  if (root.type === NODE_TYPE.CATEGORY) {
    const subcategories = await fetchChildren(root.id)
    return [root, ...subcategories]
  }

  if (root.type === NODE_TYPE.GLOBAL) {
    const categories = await fetchChildren(root.id)
    const nested = await Promise.all(categories.map((category) => fetchChildren(category.id)))
    return [root, ...categories, ...nested.flat()]
  }

  return [root]
}

export async function runCatalogTaxonomyDeletePreflight({ root, siblingsOfRoot = [] }) {
  if (!root?.id) {
    return assembleDeletePreflight({
      root: null,
      subtreeNodes: [],
      siblingsOfRoot: [],
      blockers: [],
    })
  }

  const subtreeNodes = await collectCatalogTaxonomySubtree(root)
  const blockers = await collectSubtreeBlockers(subtreeNodes)
  return assembleDeletePreflight({
    root,
    subtreeNodes,
    siblingsOfRoot,
    blockers,
  })
}

export async function createCatalogTaxonomyNode({
  type,
  parentId = null,
  name,
  fullName = '',
  slug,
  siblings = [],
  parentNode = null,
  isActive = false,
  showInNavigation = true,
}) {
  if (type !== NODE_TYPE.GLOBAL) {
    const expected = expectedParentType(type)
    if (!parentNode || parentNode.type !== expected) {
      throw new Error('Некоректний батьківський вузол для цього рівня.')
    }
    if (parentNode.isArchived === true) {
      throw new Error('Спочатку відновіть батьківський вузол з архіву.')
    }
    const parentGuard = await assertCanAddNonArchivedChildUnderParent({
      parentId: parentNode.id,
      siblingNodes: siblings,
    })
    if (!parentGuard.ok) {
      throw new Error(parentGuard.message)
    }
  }

  const resolvedSlug = slug || slugifyCatalogTaxonomyName(name)
  assertSlugAvailable(resolvedSlug, siblings)

  const result = await writeCatalogTaxonomySlug({
    action: 'create',
    type,
    parentId: type === NODE_TYPE.GLOBAL ? null : parentId,
    name,
    fullName,
    slug: resolvedSlug,
    isActive,
    showInNavigation,
  })

  if (!result.ok) {
    throw new Error(result.message || 'Не вдалося створити вузол.')
  }

  return result.data?.nodeId
}

export async function updateCatalogTaxonomyNode({
  nodeId,
  patch,
  siblings = [],
  previousNode,
}) {
  const nextName = patch.name !== undefined ? patch.name : previousNode.name
  const nextSlug = patch.slug !== undefined ? patch.slug : previousNode.slug

  if (patch.slug !== undefined || patch.name !== undefined) {
    assertSlugAvailable(nextSlug, siblings, nodeId)
  }

  if (patch.slug !== undefined) {
    const slugResult = await writeCatalogTaxonomySlug({
      action: 'setSlug',
      nodeId,
      slug: String(nextSlug || '').trim(),
    })
    if (!slugResult.ok) {
      throw new Error(slugResult.message || 'Не вдалося зберегти slug.')
    }
  }

  const flags = normalizeCatalogTaxonomyFlags({
    isArchived: patch.isArchived !== undefined ? patch.isArchived : previousNode.isArchived,
    isActive: patch.isActive !== undefined ? patch.isActive : previousNode.isActive,
    showInNavigation:
      patch.showInNavigation !== undefined ? patch.showInNavigation : previousNode.showInNavigation,
  })

  const data = {
    ...flags,
    updatedAt: serverTimestamp(),
  }

  if (patch.name !== undefined) data.name = String(nextName || '').trim()
  if (patch.fullName !== undefined) data.fullName = String(patch.fullName || '').trim()
  if (patch.photos !== undefined) data.photos = patch.photos
  // slug is never written from client — setSlug callable owns it

  const hasNonSlugPatch =
    patch.name !== undefined ||
    patch.fullName !== undefined ||
    patch.photos !== undefined ||
    patch.isArchived !== undefined ||
    patch.isActive !== undefined ||
    patch.showInNavigation !== undefined

  if (!hasNonSlugPatch && patch.slug !== undefined) {
    return 'success'
  }

  if (!hasNonSlugPatch) {
    return 'success'
  }

  const result = await updateDoc({
    path: CATALOG_TAXONOMY_COLLECTION,
    docId: nodeId,
    data,
  })

  if (result === 'fail') {
    throw new Error('Не вдалося зберегти вузол.')
  }

  return result
}

export async function archiveCatalogTaxonomyNode({ node, siblings = [] }) {
  const flags = flagsForArchiveWrite(node)

  const result = await updateDoc({
    path: CATALOG_TAXONOMY_COLLECTION,
    docId: node.id,
    data: {
      ...flags,
      updatedAt: serverTimestamp(),
    },
  })

  if (result === 'fail') {
    throw new Error('Не вдалося архівувати вузол.')
  }

  const remaining = siblings.filter((n) => n.id !== node.id && n.isArchived !== true)
  await compactNonArchivedSortOrders(remaining)
  return result
}

export async function restoreCatalogTaxonomyNode({ node, siblings = [] }) {
  const { nonArchived } = partitionSiblingLists(siblings.filter((n) => n.id !== node.id))

  if (node.parentId) {
    const parentGuard = await assertCanAddNonArchivedChildUnderParent({
      parentId: node.parentId,
      siblingNodes: siblings,
      restoringNodeId: node.id,
    })
    if (!parentGuard.ok) {
      throw new Error(parentGuard.message)
    }
  }

  const result = await updateDoc({
    path: CATALOG_TAXONOMY_COLLECTION,
    docId: node.id,
    data: {
      isArchived: false,
      sortOrder: nextSortOrder(nonArchived),
      updatedAt: serverTimestamp(),
    },
  })

  if (result === 'fail') {
    throw new Error('Не вдалося відновити вузол.')
  }

  return result
}

/**
 * Hard-delete full subtree via Admin callable (nodes + owned slug locks).
 * Then best-effort Storage cleanup on the client.
 *
 * @returns {{ rootId: string, preflight: object, storage: { deleted: string[], failed: string[] } }}
 */
export async function deleteCatalogTaxonomySubtree({ root, siblingsOfRoot = [], preflight: existingPreflight }) {
  if (!root?.id) {
    throw new Error('Немає вузла для видалення.')
  }

  const preflight =
    existingPreflight ||
    (await runCatalogTaxonomyDeletePreflight({ root, siblingsOfRoot }))

  if (!preflight.ok) {
    const first = preflight.blockers[0]
    throw new Error(first?.message || 'Видалення заборонено.')
  }

  const result = await writeCatalogTaxonomySlug({
    action: 'deleteSubtree',
    rootId: root.id,
  })

  if (!result.ok) {
    throw new Error(result.message || 'Не вдалося видалити гілку.')
  }

  const storagePaths =
    collectCatalogTaxonomyStoragePaths(result.data?.deletedNodes || preflight.nodes || []) ||
    preflight.storagePathsPreview ||
    []

  const storage = await cleanupCatalogTaxonomyStoragePaths(storagePaths, storageRef)

  return {
    rootId: root.id,
    preflight,
    storage,
  }
}

/** @deprecated use deleteCatalogTaxonomySubtree */
export async function deleteCatalogTaxonomyNode({ node, siblings = [] }) {
  return deleteCatalogTaxonomySubtree({ root: node, siblingsOfRoot: siblings })
}

export async function saveCatalogTaxonomySeo({ nodeId, seoDotPathUpdate }) {
  const result = await updateDoc({
    path: CATALOG_TAXONOMY_COLLECTION,
    docId: nodeId,
    data: {
      ...seoDotPathUpdate,
      updatedAt: serverTimestamp(),
    },
  })

  if (result === 'fail') {
    throw new Error('Не вдалося зберегти SEO.')
  }

  return result
}
